import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Popover,
  Button,
  Box,
  Chip,
  Typography,
  Divider,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { DayPicker, DateRange } from 'react-day-picker';
import { format, isValid, parse, startOfMonth } from 'date-fns';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import 'react-day-picker/dist/style.css';

interface DateRangeFilterProps {
  value: { startDate: string; endDate: string };
  onChange: (value: { startDate: string; endDate: string }) => void;
  label?: string;
}

export const DateRangeFilter: React.FC<DateRangeFilterProps> = ({
  value,
  onChange,
  label = 'Date Range',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDarkMode = theme.palette.mode === 'dark';
  const accentColor = theme.palette.primary.main;
  const accentContrast = theme.palette.getContrastText(accentColor);
  const parsedInitialRange = useMemo<DateRange | undefined>(() => {
    const startDate = value.startDate
      ? parse(value.startDate, 'yyyy-MM-dd', new Date())
      : undefined;
    const endDate = value.endDate ? parse(value.endDate, 'yyyy-MM-dd', new Date()) : undefined;

    if (startDate && isValid(startDate) && endDate && isValid(endDate)) {
      return { from: startDate, to: endDate };
    }
    return undefined;
  }, [value.startDate, value.endDate]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRange, setSelectedRange] = useState<DateRange | undefined>(parsedInitialRange);
  const [displayMonth, setDisplayMonth] = useState<Date>(() =>
    startOfMonth(parsedInitialRange?.from || new Date()),
  );

  const buttonRef = useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = () => {
    setAnchorEl(buttonRef.current);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRangeSelect = (range: DateRange | undefined) => {
    setSelectedRange(range);
    if (range?.from) {
      setDisplayMonth(startOfMonth(range.from));
    }

    if (range?.from && range?.to) {
      onChange({
        startDate: format(range.from, 'yyyy-MM-dd'),
        endDate: format(range.to, 'yyyy-MM-dd'),
      });
    } else if (range?.from && !range?.to) {
      // Single date selected, set both start and end to same date
      onChange({
        startDate: format(range.from, 'yyyy-MM-dd'),
        endDate: format(range.from, 'yyyy-MM-dd'),
      });
    }
  };

  const handleClear = () => {
    setSelectedRange(undefined);
    setDisplayMonth(startOfMonth(new Date()));
    onChange({
      startDate: '',
      endDate: '',
    });
    handleClose();
  };

  const formatDisplayValue = () => {
    if (selectedRange?.from && selectedRange?.to) {
      if (selectedRange.from.getTime() === selectedRange.to.getTime()) {
        return format(selectedRange.from, 'MMM d, yyyy');
      }
      return `${format(selectedRange.from, 'MMM d')} - ${format(selectedRange.to, 'MMM d, yyyy')}`;
    }
    return 'Select range';
  };

  const getQuickRanges = () => {
    const today = new Date();
    const quickRanges = [];

    // Generate last 6 months with most recent at the top
    for (let i = 0; i < 6; i++) {
      const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
      const startOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
      const endOfMonth = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);

      const monthName = format(monthDate, 'MMMM');

      quickRanges.push({
        label: monthName,
        range: { from: startOfMonth, to: endOfMonth },
      });
    }

    return quickRanges;
  };

  // Update internal state when props change
  useEffect(() => {
    const startDate = value.startDate
      ? parse(value.startDate, 'yyyy-MM-dd', new Date())
      : undefined;
    const endDate = value.endDate ? parse(value.endDate, 'yyyy-MM-dd', new Date()) : undefined;

    if (startDate && isValid(startDate) && endDate && isValid(endDate)) {
      setSelectedRange({ from: startDate, to: endDate });
      setDisplayMonth(startOfMonth(startDate));
    } else {
      setSelectedRange(undefined);
      setDisplayMonth(startOfMonth(new Date()));
    }
  }, [value.startDate, value.endDate]);

  return (
    <>
      <Button
        ref={buttonRef}
        onClick={handleClick}
        variant="outlined"
        size="small"
        startIcon={<CalendarTodayIcon sx={{ fontSize: 16 }} />}
        sx={{
          minWidth: 200,
          maxWidth: 300,
          height: 36,
          justifyContent: 'flex-start',
          textTransform: 'none',
          borderColor:
            theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.23)' : 'rgba(0, 0, 0, 0.23)',
          color: selectedRange ? 'text.primary' : 'text.secondary',
          '&:hover': {
            borderColor:
              theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.4)',
            backgroundColor: 'action.hover',
          },
          px: 1.5,
          display: 'flex',
          gap: 1,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            flex: 1,
            overflow: 'hidden',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.65rem',
              lineHeight: 1,
              color: 'text.secondary',
              mb: 0.25,
            }}
          >
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: '0.875rem',
              fontWeight: 500,
              color: selectedRange ? 'text.primary' : 'text.secondary',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              width: '100%',
              textAlign: 'left',
            }}
          >
            {formatDisplayValue()}
          </Typography>
        </Box>
      </Button>

      {/* Mobile: Full-screen Dialog */}
      {isMobile ? (
        <Dialog
          open={open}
          onClose={handleClose}
          fullScreen
          sx={{
            '& .MuiDialog-paper': {
              bgcolor: 'background.paper',
            },
          }}
        >
          <DialogTitle
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Select Date Range
            </Typography>
            <IconButton onClick={handleClose} aria-label="close" size="large">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ px: 2, pt: 0 }}>
            {/* Quick ranges - horizontal scroll on mobile */}
            <Typography
              variant="subtitle2"
              sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}
            >
              Quick ranges
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mb: 2,
                overflowX: 'auto',
                pb: 1,
                '&::-webkit-scrollbar': { display: 'none' },
                scrollbarWidth: 'none',
              }}
            >
              {getQuickRanges().map((quickRange) => (
                <Chip
                  key={quickRange.label}
                  label={quickRange.label}
                  onClick={() => handleRangeSelect(quickRange.range)}
                  variant={
                    selectedRange?.from &&
                    selectedRange?.to &&
                    quickRange.range.from.getTime() === selectedRange.from.getTime() &&
                    quickRange.range.to.getTime() === selectedRange.to.getTime()
                      ? 'filled'
                      : 'outlined'
                  }
                  color="primary"
                  sx={{ flexShrink: 0 }}
                />
              ))}
            </Box>

            <Divider sx={{ mb: 2 }} />

            {/* Calendar - single month on mobile */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                '& .rdp': {
                  fontSize: '14px',
                  '--rdp-cell-size': '44px', // Larger touch targets on mobile
                  '--rdp-accent-color': accentColor,
                  '--rdp-background-color': accentColor,
                  '--rdp-accent-color-dark': accentColor,
                  '--rdp-background-color-dark': accentColor,
                  '--rdp-selected-border': 'none',
                  '--rdp-outline': 'none',
                  '--rdp-outline-selected': 'none',
                  color: theme.palette.text.primary,
                  margin: '0 auto',
                },
                '& .rdp-months': {
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '1rem',
                },
                '& .rdp-month': {
                  margin: 0,
                  width: '100%',
                },
                '& .rdp-table': {
                  margin: '0.5rem 0',
                  width: '100%',
                },
                '& .rdp-head_cell': {
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: theme.palette.text.secondary,
                  padding: '0.5rem',
                },
                '& .rdp-cell': {
                  padding: 0,
                  border: 'none',
                },
                '& .rdp-day_button': {
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: 'var(--rdp-cell-size)',
                  height: 'var(--rdp-cell-size)',
                  margin: '0 auto',
                  borderRadius: '50%',
                  border: 'none',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: theme.palette.text.primary,
                  backgroundColor: 'transparent',
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.text.primary,
                  },
                  '&:focus-visible': {
                    outline: 'none',
                    boxShadow: 'none',
                  },
                },
                '& .rdp-day_button[aria-selected="true"]': {
                  backgroundColor: `${accentColor} !important`,
                  color: `${accentContrast} !important`,
                  fontWeight: 600,
                },
                '& .rdp-range_middle': {
                  backgroundColor: alpha(accentColor, isDarkMode ? 0.15 : 0.1),
                  '& .rdp-day_button': {
                    backgroundColor: 'transparent',
                    color: theme.palette.text.primary,
                    borderRadius: 0,
                    fontWeight: 400,
                  },
                },
                '& .rdp-range_start': {
                  backgroundColor: alpha(accentColor, isDarkMode ? 0.15 : 0.1),
                  borderTopLeftRadius: '22px',
                  borderBottomLeftRadius: '22px',
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  '& .rdp-day_button': {
                    backgroundColor: `${accentColor} !important`,
                    color: `${accentContrast} !important`,
                    borderRadius: '50%',
                    fontWeight: 600,
                  },
                },
                '& .rdp-range_end': {
                  backgroundColor: alpha(accentColor, isDarkMode ? 0.15 : 0.1),
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  borderTopRightRadius: '22px',
                  borderBottomRightRadius: '22px',
                  '& .rdp-day_button': {
                    backgroundColor: `${accentColor} !important`,
                    color: `${accentContrast} !important`,
                    borderRadius: '50%',
                    fontWeight: 600,
                  },
                },
                '& .rdp-range_start.rdp-range_end': {
                  backgroundColor: 'transparent',
                  borderRadius: 0,
                  '& .rdp-day_button': {
                    backgroundColor: `${accentColor} !important`,
                    color: `${accentContrast} !important`,
                    borderRadius: '50%',
                    fontWeight: 600,
                  },
                },
                '& .rdp-caption_label, & .rdp-nav_button': {
                  color: theme.palette.text.primary,
                },
                '& .rdp-nav_button': {
                  minWidth: 44,
                  minHeight: 44,
                },
                '& .rdp-nav_button:hover': {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <DayPicker
                mode="range"
                selected={selectedRange}
                onSelect={handleRangeSelect}
                numberOfMonths={1}
                month={displayMonth}
                onMonthChange={(month) => setDisplayMonth(startOfMonth(month))}
                showOutsideDays
              />
            </Box>

            {/* Selected range display */}
            {selectedRange?.from && selectedRange?.to && (
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Chip
                  label={formatDisplayValue()}
                  color="primary"
                  variant="outlined"
                  onDelete={handleClear}
                />
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2, gap: 1 }}>
            <Button
              onClick={handleClear}
              variant="outlined"
              color="secondary"
              sx={{ flex: 1, minHeight: 48 }}
            >
              Clear
            </Button>
            <Button onClick={handleClose} variant="contained" sx={{ flex: 1, minHeight: 48 }}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      ) : (
        /* Desktop: Popover */
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
          slotProps={{
            paper: {
              sx: {
                boxShadow: theme.shadows[8],
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: 'hidden',
                backgroundColor: theme.palette.background.paper,
              },
            },
          }}
        >
          <Box sx={{ display: 'flex', minWidth: 650 }}>
            {/* Quick ranges sidebar */}
            <Box
              sx={{
                width: 180,
                p: 2,
                borderRight: `1px solid ${theme.palette.divider}`,
                backgroundColor:
                  theme.palette.mode === 'dark' ? theme.palette.grey[900] : theme.palette.grey[50],
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}
              >
                Quick ranges
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {getQuickRanges().map((quickRange) => (
                  <Button
                    key={quickRange.label}
                    size="small"
                    variant="text"
                    onClick={() => handleRangeSelect(quickRange.range)}
                    sx={{
                      justifyContent: 'flex-start',
                      textTransform: 'none',
                      fontSize: '0.875rem',
                      py: 0.75,
                      px: 1,
                      color: 'text.primary',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    {quickRange.label}
                  </Button>
                ))}
              </Box>
            </Box>

            {/* Calendar */}
            <Box sx={{ p: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 2, fontWeight: 600, color: 'text.secondary' }}
              >
                Select date range
              </Typography>
              <Box
                sx={{
                  '& .rdp': {
                    fontSize: '14px',
                    '--rdp-cell-size': '36px',
                    '--rdp-accent-color': accentColor,
                    '--rdp-background-color': accentColor,
                    '--rdp-accent-color-dark': accentColor,
                    '--rdp-background-color-dark': accentColor,
                    '--rdp-selected-border': 'none',
                    '--rdp-outline': 'none',
                    '--rdp-outline-selected': 'none',
                    color: theme.palette.text.primary,
                  },
                  '& .rdp-months': {
                    display: 'flex',
                    gap: '2rem',
                  },
                  '& .rdp-month': {
                    margin: 0,
                  },
                  '& .rdp-table': {
                    margin: '0.5rem 0',
                  },
                  '& .rdp-head_cell': {
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: theme.palette.text.secondary,
                    padding: '0.5rem',
                  },
                  '& .rdp-cell': {
                    padding: 0,
                    border: 'none',
                  },
                  '& .rdp-day_button': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 'var(--rdp-cell-size)',
                    height: 'var(--rdp-cell-size)',
                    margin: '0 auto',
                    borderRadius: '50%',
                    border: 'none',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: theme.palette.text.primary,
                    backgroundColor: 'transparent',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      color: theme.palette.text.primary,
                    },
                    '&:focus-visible': {
                      outline: 'none',
                      boxShadow: 'none',
                    },
                  },
                  '& .rdp-day_button[aria-selected="true"]': {
                    backgroundColor: `${accentColor} !important`,
                    color: `${accentContrast} !important`,
                    fontWeight: 600,
                    '&:hover': {
                      backgroundColor: `${accentColor} !important`,
                      color: `${accentContrast} !important`,
                    },
                  },
                  '& .rdp-range_middle': {
                    backgroundColor: alpha(accentColor, isDarkMode ? 0.15 : 0.1),
                    '& .rdp-day_button': {
                      backgroundColor: 'transparent',
                      color: theme.palette.text.primary,
                      borderRadius: 0,
                      fontWeight: 400,
                      '&:hover': {
                        backgroundColor: alpha(accentColor, isDarkMode ? 0.25 : 0.2),
                        color: theme.palette.text.primary,
                      },
                    },
                  },
                  '& .rdp-range_start': {
                    backgroundColor: alpha(accentColor, isDarkMode ? 0.15 : 0.1),
                    borderTopLeftRadius: '18px',
                    borderBottomLeftRadius: '18px',
                    borderTopRightRadius: 0,
                    borderBottomRightRadius: 0,
                    '& .rdp-day_button': {
                      backgroundColor: `${accentColor} !important`,
                      color: `${accentContrast} !important`,
                      borderRadius: '50%',
                      fontWeight: 600,
                    },
                  },
                  '& .rdp-range_end': {
                    backgroundColor: alpha(accentColor, isDarkMode ? 0.15 : 0.1),
                    borderTopLeftRadius: 0,
                    borderBottomLeftRadius: 0,
                    borderTopRightRadius: '18px',
                    borderBottomRightRadius: '18px',
                    '& .rdp-day_button': {
                      backgroundColor: `${accentColor} !important`,
                      color: `${accentContrast} !important`,
                      borderRadius: '50%',
                      fontWeight: 600,
                    },
                  },
                  '& .rdp-range_start.rdp-range_end': {
                    backgroundColor: 'transparent',
                    borderRadius: 0,
                    '& .rdp-day_button': {
                      backgroundColor: `${accentColor} !important`,
                      color: `${accentContrast} !important`,
                      borderRadius: '50%',
                      fontWeight: 600,
                    },
                  },
                  '& .rdp-caption_label, & .rdp-nav_button': {
                    color: theme.palette.text.primary,
                  },
                  '& .rdp-nav_button:hover': {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <DayPicker
                  mode="range"
                  selected={selectedRange}
                  onSelect={handleRangeSelect}
                  numberOfMonths={2}
                  month={displayMonth}
                  onMonthChange={(month) => setDisplayMonth(startOfMonth(month))}
                  showOutsideDays
                />
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box
                sx={{
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Box>
                  {selectedRange?.from && selectedRange?.to && (
                    <Chip
                      label={formatDisplayValue()}
                      size="small"
                      color="primary"
                      variant="outlined"
                      onDelete={handleClear}
                    />
                  )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button size="small" onClick={handleClear} variant="outlined" color="secondary">
                    Clear
                  </Button>
                  <Button size="small" onClick={handleClose} variant="contained">
                    Apply
                  </Button>
                </Box>
              </Box>
            </Box>
          </Box>
        </Popover>
      )}
    </>
  );
};
